import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { ConsentUtil, historyBack } from '../../utils'
import { Customer, CustomerConsent, Consent, User, PriceLevel, Address } from 'depoto-core/src/entities'
import { CoreService, NotificationService } from '../../services'
import { SchemaUtil } from '../../utils'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { FmtDatePipe } from '../../pipes'
import { TranslateService } from '@ngx-translate/core'
import { TitleService } from '../../services'
import { TagService } from '../../services/entity-services/tag.service'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { CustomerService } from '../../services/entity-services/customer.service'
import { updateCachedListQueries } from '../../services/query-cache.service'

const customerSchema = {
  id: null,
  addresses: SchemaUtil.address,
  birthday: null,
  consentRelations: { id: null, consent: { id: null }, name: null, body: null },
  email: null,
  firstName: null,
  lastName: null,
  companyName: null,
  minExpirationDays: null,
  name: null,
  note: null,
  phone: null,
  priceLevel: { id: null, name: null, isPercentage: null, percent: null, withVat: null, currency: { id: null } },
  tags: { id: null, name: null, type: null, color: null, externalId: null },
  users: { id: null, email: null, firstName: null, lastName: null, name: null },
  wholesale: null,
}

@Component({
  selector: 'app-customer',
  templateUrl: './customer.component.html',
  styleUrls: ['./customer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CustomerComponent implements OnInit, CheckDataChanges {
  private customerService = inject(CustomerService)
  private tagService = inject(TagService)
  private queryClient = injectQueryClient()

  private initialFormValue = signal<any>(null)
  submitted = signal(false)

  consents: Consent[] = []
  salesmen: User[]
  priceLevels: PriceLevel[] = []
  newAddress: Address = new Address({ isStored: true, country: 'CZ' })
  isShowingSalesmen = true

  customerForm = new FormGroup({
    email: new FormControl({ value: '', disabled: false }, [Validators.required, Validators.email]),
    firstName: new FormControl({ value: '', disabled: false }, [Validators.required]),
    lastName: new FormControl({ value: '', disabled: false }, [Validators.required]),
    companyName: new FormControl({ value: '', disabled: false }, []),
    phone: new FormControl({ value: '', disabled: false }, []),
    addresses: new FormControl({ value: [], disabled: false }, []),
    note: new FormControl({ value: '', disabled: false }, []),
    wholesale: new FormControl({ value: false, disabled: false }, []),
    birthday: new FormControl({ value: '', disabled: false }, []),
    minExpirationDays: new FormControl({ value: 0, disabled: false }, []),
    priceLevel: new FormControl({ value: 0, disabled: false }, []),
    consentRelations: new FormControl({ value: [], disabled: false }, []),
    tags: new FormControl({ value: [], disabled: false }, []),
    users: new FormControl({ value: [], disabled: false }, []),
  })

  readonly queryTags = injectQuery(() => ({
    queryKey: ['tags', 'options'],
    queryFn: async () => {
      return await this.tagService
        .getTags({ sort: 'name', direction: 'asc' }, { id: null, name: null, type: null })
        .then(result => result.items)
    },
    select: data => (data as any[]).filter(tag => tag.type === 'customer'),
  }))

  readonly queryCustomer = injectQuery(() => ({
    queryKey: ['customer', Number(this.route.snapshot.params['id'])],
    queryFn: async () => {
      const id = Number(this.route.snapshot.params['id'])
      if (id) {
        return await this.customerService.getCustomer(id, customerSchema).then(result => {
          this.fillFormValue(result)
          this.shortenBirthdayDate()
          return result
        })
      } else {
        return new Customer()
      }
    },
    onSuccess: data => {
      this.titleService.updateTitleWithObject(
        data['id'],
        data['firstName'] + ' ' + data['lastName'],
        'title.customer-id'
      )
    },
  }))

  createCustomer = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      if (this.customerForm.invalid) {
        throw new Error('Form is invalid')
        return
      }

      this.shortenBirthdayDate()
      const n = this.getDataToSave()

      return await this.customerService.createCustomer(n, customerSchema as any)
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.customerForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.customerForm.markAsPristine()
      this.shortenBirthdayDate()
      // Update detail cache for this customer
      this.queryClient.setQueryData(['customer', Number(data.id)], data)
      // Update all cached list queries beginning with ['customers', ...] so listings reflect the change immediately
      updateCachedListQueries(this.queryClient, ['customers'], data, { mode: 'insert', insertPosition: 'start' })

      this.removeCustomerListings()

      this.notificationService.success(this.translateService.instant('alert.customer.added'))
      this.router.navigate(['/customer/', data['id']], { replaceUrl: true })
    },
  }))

  updateCustomer = injectMutation(() => ({
    mutationFn: () => {
      this.submitted.set(true)
      if (this.customerForm.invalid) {
        throw new Error('Form is invalid')
      }

      this.shortenBirthdayDate()
      const customerDataToSave = this.getDataToSave()
      return this.customerService.updateCustomer(customerDataToSave, customerSchema as any)
    },
    onSuccess: async data => {
      this.submitted.set(false)
      // this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.customerForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.customerForm.markAsPristine()
      this.shortenBirthdayDate()

      this.notificationService.success(this.translateService.instant('alert.customer.updated'))
      // Update detail cache for this customer
      this.queryClient.setQueryData(['customer', Number(data.id)], data)
      // Update all cached list queries beginning with ['customers', ...] so listings reflect the update immediately
      updateCachedListQueries(this.queryClient, ['customers'], data)

      this.removeCustomerListings()

      this.router.navigate(['/customer', data['id']])
    },
  }))

  deleteCustomer = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)

      if (!this.queryCustomer.data() || !this.queryCustomer.data().id) {
        alert(this.translateService.instant('customer.error.contact-admin'))
        return
      }

      const confirmation = confirm(
        this.translateService.instant('customer.delete.confirmation', {
          name: this.customerForm.value.firstName + ' ' + this.customerForm.value.firstName,
        })
      )
      if (confirmation) {
        const id = Number(this.route.snapshot.params['id'])
        await this.customerService.deleteCustomer({ id })
      } else {
        // todo
      }
    },
    onSuccess: () => {
      this.submitted.set(false)
      this.notificationService.success(this.translateService.instant('alert.customer.deleted'))
      // Prune deleted customer from any cached lists (supports infinite queries and arrays)
      const idStr = String(this.route.snapshot.params['id'])
      updateCachedListQueries(this.queryClient, ['customers'], { id: Number(idStr) } as any, { mode: 'delete' })
      // Remove detail cache for this customer
      this.queryClient.removeQueries({ queryKey: ['customer', Number(idStr)], exact: true })
      this.router.navigate(['/customers'], { replaceUrl: true })
    },
  }))

  isCreating = computed(() => {
    return !this.route.snapshot.params['id']
  })
  loading = computed(() => {
    return (
      this.queryCustomer.isFetching() ||
      this.queryCustomer.isPending() ||
      this.queryTags.isFetching() ||
      this.queryTags.isPending()
    )
  })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private titleService: TitleService
  ) {
    // Populate the form whenever query data becomes available/changes (works for cache hits too)
    // Note: guarded usage to avoid overriding user edits after initial load
    effect(() => {
      const d = this.queryCustomer.data()
      const id = this.route.snapshot.params['id']
      if (id && d && this.customerForm.pristine) {
        this.fillFormValue(d)
      }
    })
  }

  async ngOnInit() {
    if (this.core.services.user.hasRole('ROLE_SALESMAN') && this.isCreating)
      this.salesmen = [this.core.services.user.user]
    else
      this.salesmen = await this.core.services.user
        .getList({}, { id: null, firstName: null, lastName: null, email: null })
        .then(result => result.items)
    this.priceLevels = await this.core.services.priceLevel.getList({}, { id: null, name: null }).then(result => {
      return [new PriceLevel({ id: 0, name: 'all' }), ...result.items]
    })
    this.setIsShowingSalesmen()
  }

  goBack(): void {
    historyBack()
  }

  setIsShowingSalesmen() {
    this.isShowingSalesmen =
      this.salesmen?.length &&
      (!this.core.services.user.hasRole('ROLE_SALESMAN') ||
        (this.core.services.user.hasRole('ROLE_SALESMAN') && this.isCreating()))
  }

  public fillFormValue(customer) {
    const value = {
      email: customer.email || '',
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      companyName: customer.companyName || '',
      phone: customer.phone || '',
      addresses: customer.addresses || [],
      wholesale: customer.wholesale || false,
      birthday: customer.birthday ? this.getTransformedDate(customer.birthday) : '',
      minExpirationDays: customer.minExpirationDays || 0,
      priceLevel: customer?.priceLevel?.id || null,
      note: customer?.note || '',
      consentRelations: customer?.consentRelations || [],
      tags: customer?.tags || [],
      users: customer?.users || [],
    }

    this.customerForm.setValue(value)
    // Store initial values snapshot and reset form dirty state
    this.initialFormValue.set(structuredClone(value))
    this.customerForm.markAsPristine()
  }

  getTransformedDate(date: string) {
    const filterPipe = new FmtDatePipe()
    return filterPipe.transform(date, 'yyyy-MM-dd')
  }

  hasUnsavedDataChanges(): boolean {
    const current = this.customerForm.getRawValue() as any
    const initial = this.initialFormValue() as any

    // Fallback: if initial snapshot not ready yet
    if (!initial) {
      return this.customerForm.dirty
    }

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))

    return (
      norm(current?.name) !== norm(initial?.name) ||
      norm(current?.email ?? '') !== norm(initial?.email ?? '') ||
      // norm(current?.default ?? false) !== norm(initial?.default ?? false) ||
      norm(current?.firstName ?? '') !== norm(initial?.firstName ?? '') ||
      norm(current?.lastName ?? '') !== norm(initial?.lastName ?? '') ||
      norm(current?.companyName ?? '') !== norm(initial?.companyName ?? '') ||
      norm(current?.phone ?? '') !== norm(initial?.phone ?? '') ||
      norm(current?.addresses ?? []) !== norm(initial?.addresses ?? []) ||
      norm(current?.wholesale ?? false) !== norm(initial?.wholesale ?? false) ||
      norm(current?.birthday ?? '') !== norm(initial?.birthday ?? '') ||
      norm(current?.minExpirationDays ?? '') !== norm(initial?.minExpirationDays ?? '') ||
      norm(current?.priceLevel ?? 0) !== norm(initial?.priceLevel ?? 0) ||
      norm(current?.consentRelations ?? []) !== norm(initial?.consentRelations ?? []) ||
      norm(current?.tags ?? []) !== norm(initial?.tags ?? []) ||
      norm(current?.users ?? []) !== norm(initial?.users ?? [])
    )
  }

  async deleteCustomerConsent(customerConsent: CustomerConsent) {
    if (confirm(this.translateService.instant('customer.delete.confirmation.consent'))) {
      try {
        await this.core.services.consent.deleteCustomerConsent(customerConsent).then(() => {
          this.notificationService.success(this.translateService.instant('alert.customer.consent.deleted'))
          this.customerForm
            .get('consentRelations')
            .setValue(this.customerForm.value.consentRelations.filter(c => c.id !== customerConsent.id))
        })
      } catch (err) {
        console.error(err)
      }
    }
  }

  printCustomerConsent(customerConsent: CustomerConsent): void {
    customerConsent.customer = new Customer(this.customerForm.value)
    ConsentUtil.printCustomerConsent(customerConsent)
  }

  public isSalesmanSelected(salesman: User): boolean {
    const salesmanId = String(salesman.id)
    const users = this.customerForm.get('users')?.value ?? []
    return users.some(user => String(user.id) === salesmanId)
  }

  public toggleSalesmanSelected(salesman: User) {
    const usersControl = this.customerForm.get('users') as FormControl
    const currentUsers = Array.isArray(usersControl.value) ? usersControl.value : []
    const salesmanId = String(salesman.id)
    const nextUsers = this.isSalesmanSelected(salesman)
      ? currentUsers.filter(user => String(user.id) !== salesmanId)
      : [...currentUsers, new User({ id: salesman.id })]

    usersControl.setValue(nextUsers)
    usersControl.markAsDirty()
  }

  onTagsChange(event) {
    this.customerForm.get('tags').setValue(this.queryTags.data().filter(t => event.includes(t.id)))
  }

  shortenBirthdayDate() {
    const birthdayControl = this.customerForm.get('birthday') as FormControl
    const val: string = birthdayControl?.value
    if (val && val.length > 0) {
      birthdayControl.setValue(val.substring(0, 10))
      birthdayControl.markAsDirty()
    }
  }

  addAddress(address: Address): void {
    const addressesControl = this.customerForm.get('addresses') as FormControl
    const current: Address[] = Array.isArray(addressesControl.value) ? addressesControl.value : []
    try {
      const next = [
        ...current,
        new Address({
          ...address,
          ...{
            isStored: true,
          },
        }),
      ]
      addressesControl.setValue(next)
      addressesControl.markAsDirty()
      // this.notificationService.success(this.translateService.instant('alert.address.added'))
      // this.refreshData(this.customer.id)
      // this.queryCustomer
    } catch (err) {
      console.error(err)
    }
  }

  async deleteAddress(address) {
    if (!confirm(this.translateService.instant('customer.delete-address-confirmation'))) {
      return
    }
    try {
      const addressesControl = this.customerForm.get('addresses') as FormControl
      const current: Address[] = Array.isArray(addressesControl.value) ? addressesControl.value : []
      const next = current.filter(a => a.id !== address.id)
      addressesControl.setValue(next)
      addressesControl.markAsDirty()
      await this.core.services.address.delete(address).then(r => {
        this.notificationService.success(this.translateService.instant('ALERT ADDRESS DELETED'))
      })
    } catch (err) {
      console.error(err)
    }
  }

  getDataToSave() {
    const current = this.customerForm.getRawValue() as any
    const initial = this.initialFormValue() as any
    const patch: any = {}

    // Include id when editing
    const routeId = this.route.snapshot.params['id']
    if (routeId) patch.id = Number(routeId)

    // Required fields
    patch.name = current.name
    patch.firstName = current.firstName
    patch.lastName = current.lastName
    patch.email = current.email

    // Optional fields — include only when changed
    const changedDefault = (current?.default ?? false) !== (initial?.default ?? false)
    if (changedDefault) patch.default = current.default

    // Optional fields – include only when changed against initial snapshot
    const norm = (v: any) => (v === undefined || v === null ? '' : v)
    const idArray = (arr: any[]) =>
      Array.isArray(arr)
        ? arr.map((x: any) => String(x?.id)).filter((v: string) => v !== undefined && v !== null && v !== '')
        : []
    const eqArrayIds = (a: any[], b: any[]) => {
      const aa = idArray(a).slice().sort()
      const bb = idArray(b).slice().sort()
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }
    const eqArrayStrings = (a: any[], b: any[]) => {
      const aa = Array.isArray(a) ? a.map(String).slice().sort() : []
      const bb = Array.isArray(b) ? b.map(String).slice().sort() : []
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }

    if (norm(current.companyName) !== norm(initial?.companyName)) {
      patch.companyName = current.companyName
    }
    if (norm(current.phone) !== norm(initial?.phone)) {
      patch.phone = current.phone
    }
    if (norm(current.note) !== norm(initial?.note)) {
      patch.note = current.note
    }
    if (norm(current.wholesale) !== norm(initial?.wholesale)) {
      patch.wholesale = current.wholesale
    }
    if (norm(current.birthday) !== norm(initial?.birthday)) {
      patch.birthday = current.birthday
    }
    if (norm(current.minExpirationDays) !== norm(initial?.minExpirationDays)) {
      patch.minExpirationDays = current.minExpirationDays
    }
    if (norm(current.priceLevel) !== norm(initial?.priceLevel)) {
      patch.priceLevel = current.priceLevel
    }
    if (norm(current.consentRelations) !== norm(initial?.consentRelations)) {
      patch.consentRelations = current.consentRelations
    }

    // Arrays: include only when changed
    if (!eqArrayIds(current.addresses, initial?.addresses)) {
      patch.addresses = current.addresses || []
    }
    if (!eqArrayStrings(current.tags, initial?.tags)) {
      patch.tags = current.tags || []
    }
    if (routeId || !eqArrayIds(current.users, initial?.users)) {
      patch.users = current.users || []
    }

    return patch
  }

  private removeCustomerListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['customers'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.customerForm.controls
  }
}
